 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { DatabaseService } from '../DatabaseService';
import { generateId } from '../../db/connection';
import { components, type ComponentOption, type ComponentCategory } from '@smartphone-tycoon/shared-core';
import { componentReleaseService } from './componentReleaseService';
import type { 
  ComponentInventory, 
  DeviceInventory, 
  InventoryTransaction, 
  ComponentOrder,
  InventoryAlert,
  InventoryState,
  InventoryMetrics,
  Product
} from '@smartphone-tycoon/shared-core';
import { GAME_CONSTANTS } from '../../config/gameConstants';
import { GAME_FORMULAS } from '../../config/gameFormulas';

class InventoryManagementService {
  private static instance: InventoryManagementService;
  private static processingOrderIds: Set<string> = new Set();
  
  private constructor() {}
  
  static getInstance(): InventoryManagementService {
    if (!InventoryManagementService.instance) {
      InventoryManagementService.instance = new InventoryManagementService();
    }
    return InventoryManagementService.instance;
  }

  private findComponentByValue(componentValue: string): { component: ComponentOption; categoryId: string } | null {
    for (const category of components) {
      const component = category.options.find(opt => opt.value === componentValue);
      if (component) {
        return { component, categoryId: category.id };
      }
    }
    return null;
  }

  async initializeAllComponentInventory(): Promise<Map<string, ComponentInventory>> {
    const componentInventoryMap = new Map<string, ComponentInventory>();

    // console.log('[DEBUG] Starting component inventory initialization');

    // Load existing inventory from database
    const existingComponentInventory = await DatabaseService.getAll('component_inventory');

    // Create a map of existing inventory by component value
    const existingByComponentValue = new Map<string, ComponentInventory>();
    for (const inv of existingComponentInventory) {
      existingByComponentValue.set(inv.componentId, inv);
    }

    // Get available components from componentReleaseService (single source of truth)
    const availableComponentsMap = componentReleaseService.getAllAvailableComponents(2025, 1);
    const categoryMetadata = components; // Still use this for category metadata like icons

    // Iterate through all component categories and options from release service
    for (const [categoryId, categoryComponents] of availableComponentsMap) {
      const category = categoryMetadata.find(c => c.id === categoryId);
      if (!category) continue;

      // console.log(`[DEBUG] Processing category: ${categoryId} (${category.name})`);
      for (const component of categoryComponents) {
        // Use component.value as the identifier
        const componentValue = component.value;
        // console.log(`[DEBUG] - Component: ${component.name}, value: ${componentValue}`);
        
        // Check if inventory already exists
        let inventory = existingByComponentValue.get(componentValue);

        if (!inventory) {
          // Create new inventory entry with 0 stock
          const inventoryId = generateId('inv-comp-');

          inventory = {
            id: inventoryId,
            componentId: componentValue, // Use component.value as ID
            componentName: component.name,
            category: categoryId,
            currentStock: 0, // Always start with 0 stock
            minStock: GAME_CONSTANTS.inventory.components.minStockLevel,
            maxStock: GAME_CONSTANTS.inventory.components.maxStockLevel,
            unitCost: component.cost,
            consumptionRate: 0,
            leadTimeDays: GAME_CONSTANTS.inventory.components.leadTimeDays
          };
          
          // Store in database
          await DatabaseService.add('component_inventory', inventory);
        }
        
        // Add to map using component.value
        componentInventoryMap.set(componentValue, inventory);
      }
    }
    
    // console.log('[InventoryService] Component inventory initialized:', {
    //   totalComponents: componentInventoryMap.size,
    //   existingCount: existingComponentInventory.length,
    //   newCount: componentInventoryMap.size - existingComponentInventory.length
    // });
    
    return componentInventoryMap;
  }

  async initializeInventory(products: Product[]): Promise<InventoryState> {
    const state: InventoryState = {
      components: new Map(),
      devices: new Map(),
      transactions: [],
      alerts: [],
      pendingOrders: []
    };

    // First, initialize all components from the catalog
    state.components = await this.initializeAllComponentInventory();
    // console.log('[InventoryService] 🔧 Components initialized:', state.components.size);
    
    // Load existing device inventory from database
    const existingDeviceInventory = await DatabaseService.getAll('device_inventory');
    // console.log('[InventoryService] 📋 Existing device inventory loaded:', existingDeviceInventory.length);

    // Create a map of existing device inventory by product ID
    const existingByProductId = new Map<string, DeviceInventory>();
    for (const inv of existingDeviceInventory) {
      existingByProductId.set(inv.productId, inv);
    }
    
    // Initialize device inventory for all products
    let newDevicesCreated = 0;
    for (const product of products) {
      // Always create device inventory for all products regardless of status
      // Check if inventory already exists
      let deviceInventory = existingByProductId.get(product.id);
      
      if (!deviceInventory) {
        // Create new inventory entry
        const inventoryId = generateId('inv-dev-');
        
        deviceInventory = {
          id: inventoryId,
          productId: product.id,
          productName: product.name,
          currentStock: this.calculateInitialDeviceStock(product),
          productionRate: this.calculateProductionRate(product),
          targetStock: GAME_CONSTANTS.inventory.devices.targetStockLevel,
          qualityScore: product.techScore
        };
        
        // Store in database with the unique ID
        await DatabaseService.add('device_inventory', deviceInventory);
        newDevicesCreated++;
        
        // console.log('[InventoryService] ✅ Created new device inventory:', {
        //   productId: product.id,
        //   productName: product.name,
        //   inventoryId: inventoryId
        // });
      } else {
        // console.log('[InventoryService] 📦 Existing device inventory:', {
        //   productId: product.id,
        //   productName: product.name,
        //   currentStock: deviceInventory.currentStock,
        //   unitValue: deviceInventory.unitValue
        // });
      }
      
      // Store in state map using productId for easy lookup
      state.devices.set(product.id, deviceInventory);
    }
    
    // console.log('[InventoryService] 📊 Device inventory summary:', {
    //   totalDevices: state.devices.size,
    //   existingDevices: existingDeviceInventory.length,
    //   newDevicesCreated
    // });

    return state;
  }

  private calculateInitialStock(_component: ComponentOption): number {
    // Start with 0 inventory - players must order components
    return 0;
  }

  private calculateInitialDeviceStock(_product: Product): number {
    // Start with 0 inventory - devices must be produced
    return 0;
  }

  private calculateProductionRate(product: Product): number {
    // Base rate modified by product appeal
    const baseRate = GAME_CONSTANTS.inventory.devices.baseProductionRate;
    const appealModifier = product.marketAppeal / 100;
    return Math.floor(baseRate * appealModifier);
  }

  async updateComponentStock(
    componentValue: string, 
    quantity: number, 
    transactionType: 'order' | 'consumption' | 'adjustment'
  ): Promise<void> {
    const inventory = await this.getComponentInventoryByValue(componentValue);
    if (!inventory) {
      throw new Error(`Component ${componentValue} not found in inventory`);
    }

    const previousStock = inventory.currentStock;
    const newStock = inventory.currentStock + quantity;
    // console.log('[InventoryService] updateComponentStock:', {
    //   component: inventory.componentName,
    //   previousStock,
    //   quantityChange: quantity,
    //   newStock,
    //   transactionType
    // });
    
    // Prevent negative stock
    if (newStock < 0) {
      throw new Error(`Insufficient stock for ${inventory.componentName}. Available: ${inventory.currentStock}, Requested: ${Math.abs(quantity)}`);
    }
    
    inventory.currentStock = newStock;
    
    // Create transaction record
    const transaction: InventoryTransaction = {
      id: `trans-${Date.now()}`,
      type: transactionType,
      itemType: 'component',
      itemId: componentValue,
      itemName: inventory.componentName,
      quantity,
      unitCost: inventory.unitCost,
      totalCost: Math.abs(quantity) * inventory.unitCost,
      date: new Date()
    };

    await DatabaseService.update('component_inventory', inventory.id, {
      currentStock: inventory.currentStock
    });
    await DatabaseService.add('inventory_transactions', transaction);

    // Check for alerts
    if (inventory.currentStock < inventory.minStock) {
      await this.createAlert(
        inventory.currentStock === 0 ? 'out_of_stock' : 'low_stock',
        'component',
        componentValue,
        inventory.componentName
      );
    }
  }

  async placeComponentOrder(componentValue: string, quantity: number, gameDate: { day: number, month: number, year: number }): Promise<ComponentOrder> {
    // console.log('[InventoryService] Placing order:', {
    //   componentValue,
    //   quantity,
    //   gameDate,
    //   timestamp: new Date().toISOString()
    // });

    const inventory = await this.getComponentInventoryByValue(componentValue);
    
    // Component should already exist from initialization, but check just in case
    if (!inventory) {
      console.error('[InventoryService] Component not in inventory:', componentValue);
      throw new Error(`Component ${componentValue} not found in inventory. Please refresh inventory.`);
    }

    // Calculate game delivery date
    const gameDeliveryDate = {
      day: gameDate.day + inventory.leadTimeDays,
      month: gameDate.month,
      year: gameDate.year
    };

    // Adjust for month/year overflow
    if (gameDeliveryDate.day > 30) {
      gameDeliveryDate.day -= 30;
      gameDeliveryDate.month += 1;
      if (gameDeliveryDate.month > 12) {
        gameDeliveryDate.month = 1;
        gameDeliveryDate.year += 1;
      }
    }

    // console.log('[InventoryService] Calculated delivery date:', {
    //   orderDate: gameDate,
    //   leadTime: inventory.leadTimeDays,
    //   deliveryDate: gameDeliveryDate
    // });

    const order: ComponentOrder = {
      orderId: generateId('order-'),
      componentId: componentValue, // Use component.value as ID
      componentName: inventory.componentName,
      quantity,
      unitCost: inventory.unitCost,
      totalCost: quantity * inventory.unitCost,
      orderDate: new Date(),
      expectedDeliveryDate: new Date(Date.now() + inventory.leadTimeDays * 24 * 60 * 60 * 1000),
      status: 'pending',
      gameOrderDate: gameDate,
      gameDeliveryDate: gameDeliveryDate
    };

    await DatabaseService.add('component_orders', order);
    
    // console.log('[InventoryService] Order created:', {
    //   orderId: order.orderId,
    //   componentName: order.componentName,
    //   quantity: order.quantity,
    //   totalCost: order.totalCost,
    //   status: order.status
    // });
    
    // Update last order info
    inventory.lastOrderDate = order.orderDate;
    inventory.lastOrderQuantity = quantity;
    await DatabaseService.update('component_inventory', inventory.id, {
      lastOrderDate: inventory.lastOrderDate,
      lastOrderQuantity: inventory.lastOrderQuantity
    });

    return order;
  }

  async updateDeviceStock(productId: string, quantity: number, isProduction: boolean, unitValue?: number): Promise<void> {
    const inventory = await this.getDeviceInventoryByProductId(productId);
    if (!inventory) {
      throw new Error(`Device ${productId} not found in inventory`);
    }

    const previousStock = inventory.currentStock;
    inventory.currentStock += quantity;
    
    // console.log('[InventoryService] 📦 Device Stock Update:', {
    //   productId,
    //   productName: inventory.productName,
    //   previousStock,
    //   quantityChange: quantity,
    //   newStock: inventory.currentStock,
    //   isProduction,
    //   operation: isProduction ? 'PRODUCTION' : quantity > 0 ? 'ADJUSTMENT+' : 'SALES/ADJUSTMENT-',
    //   unitValue,
    //   timestamp: new Date().toISOString()
    // });
    
    // Update unit value if provided (e.g., from production cost)
    if (unitValue !== undefined && unitValue > 0) {
      inventory.unitValue = unitValue;
    }
    
    // Calculate inventory value
    if (inventory.unitValue) {
      inventory.inventoryValue = inventory.currentStock * inventory.unitValue;
    }
    
    const transaction: InventoryTransaction = {
      id: `trans-${Date.now()}`,
      type: isProduction ? 'production' : 'adjustment',
      itemType: 'device',
      itemId: productId,
      itemName: inventory.productName,
      quantity,
      unitCost: inventory.unitValue,
      totalCost: inventory.unitValue ? Math.abs(quantity) * inventory.unitValue : undefined,
      date: new Date()
    };

    await DatabaseService.update('device_inventory', inventory.id, inventory);
    await DatabaseService.add('inventory_transactions', transaction);
    
    // Sync to Zustand store
    const { useInventoryStore } = await import('../../stores/inventoryStore');
    const store = useInventoryStore.getState();
    store.updateDeviceInventory(
      productId,
      inventory.currentStock,
      'set',
      {
        id: inventory.id,
        productId: inventory.productId,
        productName: inventory.productName,
        currentStock: inventory.currentStock,
        productionRate: inventory.productionRate,
        targetStock: inventory.targetStock,
        qualityScore: inventory.qualityScore,
        unitValue: inventory.unitValue,
        inventoryValue: inventory.inventoryValue
      }
    );
    
    // console.log('[InventoryService] ✅ Device inventory synced to store:', {
    //   productId,
    //   storeHasDevice: store.devices.has(productId),
    //   currentStockInStore: store.devices.get(productId)?.currentStock
    // });
  }

  async consumeComponentsForProduction(productId: string, unitsToProduce: number): Promise<void> {
    const product = await DatabaseService.get('products', productId);
    if (!product) {
      throw new Error(`Product ${productId} not found`);
    }

    // First check if all components are available
    const componentsToConsume: Array<{ componentId: string; quantity: number }> = [];
    for (const componentId of Object.values(product.designConfig.components)) {
      const inventory = await this.getComponentInventoryByValue(componentId as string);
      if (!inventory || inventory.currentStock < unitsToProduce) {
        throw new Error(
          `Insufficient ${inventory?.componentName || componentId} for production. ` +
          `Available: ${inventory?.currentStock || 0}, Needed: ${unitsToProduce}`
        );
      }
      componentsToConsume.push({ componentId: componentId as string, quantity: unitsToProduce });
    }

    // Consume components based on product design
    for (const { componentId, quantity } of componentsToConsume) {
      await this.updateComponentStock(componentId, -quantity, 'consumption');
    }
  }

  async calculateConsumptionRates(products: Product[]): Promise<Map<string, number>> {
    const consumptionRates = new Map<string, number>();
    
    for (const product of products) {
      if (product.status === 'production') {
        const productionRate = this.calculateProductionRate(product);
        
        // Each component is consumed at the production rate
        Object.values(product.designConfig.components).forEach(componentValue => {
          // Use component.value as the key for backward compatibility
          const currentRate = consumptionRates.get(componentValue as string) || 0;
          consumptionRates.set(componentValue as string, currentRate + productionRate);
        });
      }
    }
    
    return consumptionRates;
  }

  async calculateInventoryMetrics(state: InventoryState, wipValue?: number): Promise<InventoryMetrics> {
    let totalComponentValue = 0;
    let totalDeviceValue = 0;
    let monthlyComponentCost = 0;
    const stockoutRisk = new Map<string, number>();

    // Calculate component metrics
    for (const [componentId, inventory] of state.components) {
      totalComponentValue += inventory.currentStock * inventory.unitCost;
      monthlyComponentCost += inventory.consumptionRate * inventory.unitCost;
      
      // Calculate stockout risk
      if (inventory.consumptionRate > 0) {
        const risk = GAME_FORMULAS.inventory.getStockoutRisk(inventory.currentStock, inventory.consumptionRate);
        stockoutRisk.set(componentId, risk);
      }
    }

    // Calculate device metrics
    for (const inventory of state.devices.values()) {
      // Use actual inventory value if available, otherwise estimate
      if (inventory.inventoryValue !== undefined) {
        totalDeviceValue += inventory.inventoryValue;
      } else if (inventory.unitValue !== undefined) {
        totalDeviceValue += inventory.currentStock * inventory.unitValue;
      } else {
        // Fallback estimate based on average component cost
        const avgComponentCost = state.components.size > 0 
          ? totalComponentValue / Array.from(state.components.values()).reduce((sum, inv) => sum + inv.currentStock, 0) 
          : 100;
        const estimatedUnitValue = avgComponentCost * 8 * 1.5; // 8 components * 1.5x markup
        totalDeviceValue += inventory.currentStock * estimatedUnitValue;
      }
    }

    // Add work-in-progress value if provided
    const totalWipValue = wipValue || 0;

    return {
      totalComponentValue,
      totalDeviceValue: totalDeviceValue + totalWipValue,
      totalInventoryValue: totalComponentValue + totalDeviceValue + totalWipValue,
      componentTurnoverRate: totalComponentValue > 0 ? monthlyComponentCost / totalComponentValue : 0,
      deviceTurnoverRate: 0.5, // Placeholder - could be calculated from sales data
      stockoutRisk,
      monthlyComponentCost,
      monthlyStorageCost: (totalComponentValue + totalDeviceValue + totalWipValue) * 0.01 // 1% storage cost
    };
  }

  private async createAlert(
    type: 'low_stock' | 'out_of_stock' | 'overstock' | 'order_delayed',
    itemType: 'component' | 'device',
    itemId: string,
    itemName: string
  ): Promise<void> {
    const alert: InventoryAlert = {
      id: `alert-${Date.now()}`,
      type,
      severity: type === 'out_of_stock' ? 'high' : type === 'low_stock' ? 'medium' : 'low',
      itemType,
      itemId,
      itemName,
      message: this.getAlertMessage(type, itemName),
      date: new Date(),
      isResolved: false
    };

    await DatabaseService.add('inventory_alerts', alert);
  }

  private getAlertMessage(type: string, itemName: string): string {
    switch (type) {
      case 'out_of_stock':
        return `${itemName} is out of stock! Production will be halted.`;
      case 'low_stock':
        return `${itemName} stock is running low. Consider placing an order.`;
      case 'overstock':
        return `${itemName} inventory levels are above maximum. Consider reducing orders.`;
      case 'order_delayed':
        return `Order for ${itemName} has been delayed.`;
      default:
        return `Inventory alert for ${itemName}`;
    }
  }

  async getAllComponentCategories(): Promise<ComponentCategory[]> {
    return components;
  }

  async validateComponentInventory(): Promise<{ valid: boolean; issues: string[] }> {
    const issues: string[] = [];
    const inventories = await DatabaseService.getAll('component_inventory');
    
    // Check for duplicates
    const componentCounts = new Map<string, number>();
    for (const inv of inventories) {
      const count = componentCounts.get(inv.componentId) || 0;
      componentCounts.set(inv.componentId, count + 1);
    }
    
    for (const [componentId, count] of componentCounts) {
      if (count > 1) {
        issues.push(`Component ${componentId} has ${count} inventory entries (should have exactly 1)`);
      }
    }
    
    // Check for missing components
    for (const category of components) {
      for (const component of category.options) {
        if (!componentCounts.has(component.value)) {
          issues.push(`Component ${component.value} (${component.name}) is missing from inventory`);
        }
      }
    }
    
    return {
      valid: issues.length === 0,
      issues
    };
  }

  private async getComponentInventoryByValue(componentValue: string): Promise<ComponentInventory | null> {
    // console.log('[DEBUG] Looking up component by value:', componentValue);
    
    // Query by componentId field (which now stores component.value)
    const inventories = await DatabaseService.getAll('component_inventory');
    // console.log('[DEBUG] All inventory componentIds:', inventories.map(inv => inv.componentId));
    const matchingInventories = inventories.filter(inv => inv.componentId === componentValue);

    // console.log('[DEBUG] Found inventories:', {
    //   componentValue,
    //   totalInventories: inventories.length,
    //   matchingCount: matchingInventories.length
    // });
    
    // Ensure only one entry exists
    if (matchingInventories.length > 1) {
      // console.warn(`[InventoryService] Found ${matchingInventories.length} inventory entries for component ${componentValue}. Using first entry.`);
      // In production, this should trigger a cleanup process
    }
    
    const result = matchingInventories[0] || null;
    if (!result) {
      console.error('[InventoryService] No inventory found for componentValue:', componentValue);
    }
    
    return result;
  }

  private async getDeviceInventoryByProductId(productId: string): Promise<DeviceInventory | null> {
    // Query by productId field
    const inventories = await DatabaseService.getAll('device_inventory');
    const found = inventories.find(inv => inv.productId === productId) || null;
    
    if (!found) {
      console.log('[InventoryService] ⚠️ Device inventory not found for product:', productId);
    }
    
    return found;
  }

  async processPendingOrders(currentGameDate: { day: number, month: number, year: number }): Promise<Array<{
    orderId: string;
    componentId: string;
    componentName: string;
    quantity: number;
    delivered: boolean;
  }>> {
    // console.log('[InventoryService] processPendingOrders called for game date:', `${currentGameDate.year}-${currentGameDate.month}-${currentGameDate.day}`, 'at real time:', new Date().toISOString());
    
    const allOrders = await DatabaseService.getAll('component_orders');
    const pendingOrders = allOrders.filter(order => order.status === 'pending');
    const deliveredOrders = [];
    
    // Use static set to track orders being processed across multiple calls
    // This prevents duplicate deliveries if the function is called multiple times
    const processingOrderIds = InventoryManagementService.processingOrderIds;

    // console.log('[InventoryService] Found orders:', {
    //   total: allOrders.length,
    //   pending: pendingOrders.length,
    //   currentDate: currentGameDate
    // });

    for (const order of pendingOrders) {
      // console.log('[DEBUG] Checking order:', {
      //   orderId: order.orderId,
      //   componentName: order.componentName,
      //   deliveryDate: order.gameDeliveryDate ? `${order.gameDeliveryDate.year}-${order.gameDeliveryDate.month}-${order.gameDeliveryDate.day}` : 'N/A',
      //   currentDate: `${currentGameDate.year}-${currentGameDate.month}-${currentGameDate.day}`,
      //   daysUntilDelivery: this.calculateDaysRemaining(currentGameDate, order.gameDeliveryDate)
      // });

      // Skip if we're already processing this order
      if (processingOrderIds.has(order.orderId)) {
        // console.log('[DEBUG] Skipping order already being processed:', order.orderId);
        continue;
      }
      
      // Check if the order should be delivered based on game date
      if (order.gameDeliveryDate &&
          (order.gameDeliveryDate.year < currentGameDate.year ||
           (order.gameDeliveryDate.year === currentGameDate.year &&
            order.gameDeliveryDate.month < currentGameDate.month) ||
           (order.gameDeliveryDate.year === currentGameDate.year &&
            order.gameDeliveryDate.month === currentGameDate.month &&
            order.gameDeliveryDate.day <= currentGameDate.day))) {
        
        // console.log('[InventoryService] ⏰ DELIVERY DATE REACHED! Order:', order.orderId, 'Delivery date:', `${order.gameDeliveryDate.year}-${order.gameDeliveryDate.month}-${order.gameDeliveryDate.day}`, 'matches current date:', `${currentGameDate.year}-${currentGameDate.month}-${currentGameDate.day}`);
        
        // Mark as processing to prevent duplicate processing
        processingOrderIds.add(order.orderId);
        
        // Order componentId is already component.value
        const componentValue = order.componentId;
        
        // Get current stock before delivery
        // const currentStock = this.getComponentStock(componentValue);
        // console.log('[DEBUG] 📦 DELIVERING ORDER:', {
        //   orderId: order.orderId,
        //   componentId: order.componentId,
        //   componentName: order.componentName,
        //   componentValue,
        //   quantity: order.quantity,
        //   currentStockBefore: currentStock,
        //   expectedStockAfter: currentStock + order.quantity
        // });
        
        // Ensure all database operations complete atomically
        try {
          // First check if this order has already been processed today
          const updatedOrder = await DatabaseService.get('component_orders', order.orderId);
          if (updatedOrder && updatedOrder.status === 'delivered') {
            console.log('[DEBUG] Order already delivered, skipping:', order.orderId);
            continue;
          }
          
          // Update order status FIRST to prevent duplicate processing
          order.status = 'delivered';
          order.deliveryDate = new Date();
          await DatabaseService.update('component_orders', order.orderId, order);
          
          // Then update the stock
          // console.log('[InventoryService] Updating stock for order:', order.orderId, 'component:', componentValue, 'quantity:', order.quantity);
          await this.updateComponentStock(componentValue, order.quantity, 'order');
          
          // Update Zustand store with new stock level
          const { useInventoryStore } = await import('../../stores/inventoryStore');
          const store = useInventoryStore.getState();
          const updatedInventory = await this.getComponentInventoryByValue(componentValue);
          if (updatedInventory) {
            store.updateInventory(
              componentValue,
              updatedInventory.currentStock,
              'set',
              {
                name: updatedInventory.componentName,
                category: updatedInventory.category,
                unitCost: updatedInventory.unitCost
              }
            );
            // console.log('[InventoryService] Updated Zustand store for component:', componentValue, 'new stock:', updatedInventory.currentStock);
          }
          
          // console.log('[DEBUG] Order database updates completed for:', order.orderId);
        } catch (error) {
          console.error('[DEBUG] Failed to deliver order:', order.orderId, error);
          // Revert order status if stock update failed
          order.status = 'pending';
          order.deliveryDate = undefined;
          await DatabaseService.update('component_orders', order.orderId, order);
          processingOrderIds.delete(order.orderId);
          continue;
        }
        
        // Get new stock after delivery
        // const newStock = this.getComponentStock(componentValue);
        // console.log('[DEBUG] ✅ ORDER DELIVERED SUCCESSFULLY:', {
        //   orderId: order.orderId,
        //   componentId: order.componentId,
        //   componentName: order.componentName,
        //   quantityDelivered: order.quantity,
        //   stockBefore: currentStock,
        //   stockAfter: newStock,
        //   stockIncreased: newStock - currentStock
        // });
        
        deliveredOrders.push({
          orderId: order.orderId,
          componentId: order.componentId,
          componentName: order.componentName,
          quantity: order.quantity,
          delivered: true
        });
        
        // Remove from processing set after successful delivery
        processingOrderIds.delete(order.orderId);
      } else {
        // const daysRemaining = this.calculateDaysRemaining(currentGameDate, order.gameDeliveryDate);
        // console.log('[DEBUG] Order not ready yet:', {
        //   orderId: order.orderId,
        //   componentName: order.componentName,
        //   currentDate: `${currentGameDate.year}-${currentGameDate.month}-${currentGameDate.day}`,
        //   deliveryDate: order.gameDeliveryDate ? `${order.gameDeliveryDate.year}-${order.gameDeliveryDate.month}-${order.gameDeliveryDate.day}` : 'N/A',
        //   daysRemaining: daysRemaining
        // });
      }
    }

    // console.log('[InventoryService] Orders processed:', {
    //   delivered: deliveredOrders.length,
    //   stillPending: pendingOrders.length - deliveredOrders.length,
    //   deliveredOrderIds: deliveredOrders.map(o => o.orderId)
    // });

    return deliveredOrders;
  }

  private calculateDaysRemaining(currentDate: { day: number, month: number, year: number }, deliveryDate: { day: number, month: number, year: number } | undefined): number {
    if (!deliveryDate) return -1;
    
    const currentTotalDays = currentDate.year * 360 + currentDate.month * 30 + currentDate.day;
    const deliveryTotalDays = deliveryDate.year * 360 + deliveryDate.month * 30 + deliveryDate.day;
    
    return deliveryTotalDays - currentTotalDays;
  }

  async getPendingOrders(): Promise<ComponentOrder[]> {
    const allOrders = await DatabaseService.getAll('component_orders');
    return allOrders.filter(order => order.status === 'pending');
  }

  async syncComponentInventoryToStore(): Promise<void> {
    // Load all component inventory from database
    const inventories = await DatabaseService.getAll('component_inventory');

    // Import the inventory store dynamically to avoid circular dependencies
    const { useInventoryStore } = await import('../../stores/inventoryStore');
    const store = useInventoryStore.getState();

    // Convert database records to ComponentInventory format and load atomically
    // This uses Zustand's set() to trigger proper re-renders in all subscribed components
    const componentsList = inventories.map(inventory => ({
      componentId: inventory.componentId,
      componentName: inventory.componentName,
      category: inventory.category,
      quantity: inventory.currentStock,
      unitCost: inventory.unitCost,
      totalValue: inventory.currentStock * inventory.unitCost,
      minStockLevel: inventory.minStock || GAME_CONSTANTS.inventory.components.minStockLevel,
      maxStockLevel: inventory.maxStock || GAME_CONSTANTS.inventory.components.maxStockLevel,
      reorderPoint: inventory.minStock || GAME_CONSTANTS.inventory.components.minStockLevel,
      reorderQuantity: (inventory.maxStock || GAME_CONSTANTS.inventory.components.maxStockLevel) -
                       (inventory.minStock || GAME_CONSTANTS.inventory.components.minStockLevel),
      lastUpdated: Date.now(),
      qualityScore: 1.0,
    }));

    store.loadComponents(componentsList);

    // console.log('[InventoryService] Synced', inventories.length, 'components from database to store');
  }

  async syncDeviceInventoryToStore(): Promise<void> {
    // Load all device inventory from database
    const deviceInventories = await DatabaseService.getAll('device_inventory');
    
    // console.log('[InventoryService] 🔄 Syncing device inventory to store:', {
    //   deviceCount: deviceInventories.length,
    //   devices: deviceInventories.map(d => ({
    //     productId: d.productId,
    //     productName: d.productName,
    //     currentStock: d.currentStock,
    //     unitValue: d.unitValue
    //   }))
    // });
    
    // Import the inventory store dynamically to avoid circular dependencies
    const { useInventoryStore } = await import('../../stores/inventoryStore');
    const store = useInventoryStore.getState();
    
    // Load devices into store
    store.loadDevices(deviceInventories);
    
    // console.log('[InventoryService] ✅ Device sync complete:', {
    //   storeDeviceCount: store.devices.size,
    //   deviceIds: Array.from(store.devices.keys())
    // });
  }

  static clearProcessingOrders(): void {
    // Clear the static processing orders set when starting a new game
    InventoryManagementService.processingOrderIds.clear();
    // console.log('[InventoryService] Cleared processing orders tracker');
  }
}

export const inventoryManagementService = InventoryManagementService.getInstance();
export { InventoryManagementService };